<template>
  <rest-list :payload="fetchEndpoint" ref="rest-list">
    <template>
      <rest-list-column
        prop="name"
        :title="$t('Automations_Field_Name')"
        :route="rowClickRoute"
      />

      <rest-list-column-enum
        prop="source.type"
        :sortable="false"
        :title="$t('Automations_Field_Source')"
        :enum="{
          fs: 'Imports_SourceType_Label_FileStorage',
          db: 'Imports_SourceType_Label_Db'
        }"
        :width="120"
      />

      <rest-list-column-enum
        prop="schedule.frequency"
        :sortable="false"
        :title="$t('Automations_Field_Frequency')"
        :enum="FrequencyTypeLabel"
        :width="120"
      />

      <rest-list-column-enum
        prop="active"
        :sortable="false"
        :title="$t('Automations_Field_Status')"
        :enum="AutomationStatusLabel"
        :width="120"
      />

      <rest-list-column
        prop="last_run"
        :title="$t('Automations_Field_LastRunDate')"
        :filterable="false"
        :filter-input="false"
        :width="200"
      >
        <template slot-scope="scope">
          <template v-if="scope.row.last_run">
            <router-link :to="taskRoute(scope.row.last_run)" class="taskLink">
              <el-link
                :type="taskStatusDetails(scope.row.last_run.status).type"
                :icon="taskStatusDetails(scope.row.last_run.status).icon"
                :underline="false"
              />
              {{ dateFormatter(scope.row.last_run.create_time) }}
            </router-link>
          </template>
          <template v-else>
            -
          </template>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="runningTasks"
        :sortable="false"
        :filterable="false"
        :title="$t('Automations_Field_RunningTasks')"
        :width="120"
      >
        <template slot-scope="scope">
          <router-link
            v-if="
              scope.row.import_task_stats &&
                scope.row.import_task_stats.running > 0
            "
            :to="rowRunHistoryTasksRoute(scope.row)"
          >
            <el-tag size="small" type="warning" effect="plain">
              <i class="el-icon-loading"></i>
              {{ scope.row.import_task_stats.running }}
              {{ $t('Automations_Field_ActiveImportTasks_Running') }}
            </el-tag>
          </router-link>
          <span v-else>-</span>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="failedTasks"
        :sortable="false"
        :filterable="false"
        :title="$t('Automations_Field_FailedTasks')"
        :width="120"
      >
        <template slot-scope="scope">
          <router-link
            v-if="
              scope.row.import_task_stats &&
                scope.row.import_task_stats.failed > 0
            "
            :to="rowRunHistoryTasksRoute(scope.row)"
          >
            <el-tag size="small" type="danger" effect="plain">
              <i class="el-icon-error"></i>
              {{ scope.row.import_task_stats.failed }}
              {{ $t('Automations_Field_ActiveImportTasks_Failed') }}
            </el-tag>
          </router-link>
          <span v-else>-</span>
        </template>
      </rest-list-column>

      <rest-list-column-actions :editable="false">
        <template slot-scope="scope">
          <rest-list-action-view
            v-if="readonly"
            :route="rowViewRoute(scope.row)"
          />
          <rest-list-action-update
            v-if="!readonly"
            :route="rowUpdateRoute(scope.row)"
          />
          <rest-list-action
            :icon="automationStatus(scope.row).icon"
            :title="automationStatus(scope.row).title"
            :click="() => toggleAutomationStatus(scope.row)"
            data-qa="toggle-action-button"
          />
          <rest-list-action
            icon="el-icon-time"
            :title="$t('Automations_RunHistory')"
            :route="rowRunHistoryRoute(scope.row)"
            data-qa="run-history-action-button"
          />
          <rest-list-action
            icon="el-icon-refresh-right"
            :title="$t('Automations_RunNow')"
            :click="() => runAutomation(scope.row)"
            data-qa="run-now-action-button"
          />
          <rest-list-action
            v-if="scope.row.source && scope.row.source.sync_mode === 'delta'"
            icon="el-icon-refresh"
            :title="$t('Automations_ResetDelta')"
            :click="() => resetDelta(scope.row)"
            data-qa="reset-delta-action-button"
          />
          <rest-list-action-delete
            :endpoint="rowDeleteEndpoint(scope.row)"
            :delete-message="
              $t('Automations_DeletePrompt', {
                label: scope.row.name
              })
            "
          />
        </template>
      </rest-list-column-actions>
    </template>
  </rest-list>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListActionView from '@/common/components/rest/RestListActionView'
import RestListActionUpdate from '@/common/components/rest/RestListActionUpdate'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import {
  projectEndpoint,
  projectTimezoneDate,
  projectRoute,
  projectUrl
} from '@/core/helpers'
import { TargetLabel } from '@/modules/automations/enum/TargetEnum'
import { FrequencyTypeLabel } from '@/modules/automations/enum/RecurrenceEnum'
import { AutomationStatusLabel } from '@/modules/automations/enum/AutomationStatusEnum'
import RestListAction from '@/common/components/rest/RestListAction'
import RestApi from '@/core/services/RestApi'
import {
  taskStatusEnum,
  taskStatusLabel
} from '@/modules/tasks/enum/TaskRecordEnums'

export default {
  name: 'AutomationImportsList',
  data() {
    return {
      TargetLabel,
      taskStatusLabel,
      taskStatusEnum,
      FrequencyTypeLabel,
      AutomationStatusLabel
    }
  },

  components: {
    RestListAction,
    RestListColumnEnum,
    RestListColumn,
    RestListColumnActions,
    RestList,
    RestListActionView,
    RestListActionUpdate,
    RestListActionDelete
  },

  computed: {
    restList() {
      return this.$refs['rest-list']
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `import-automations`,
        params: {
          expand: 'last_run,import_task_stats'
        }
      })
    },

    readonly() {
      return !this.$can('manageAutomation')
    }
  },

  methods: {
    taskStatusDetails(status) {
      if (
        status === this.taskStatusEnum.NEW ||
        status === this.taskStatusEnum.EXECUTING
      ) {
        return {
          icon: 'el-icon-loading',
          type: 'warning',
          message: this.$t('TaskHistory_StatusLoading')
        }
      } else if (
        status === this.taskStatusEnum.ERROR ||
        status === this.taskStatusEnum.FATAL
      ) {
        return {
          icon: 'el-icon-error',
          type: 'danger',
          message: this.$t(this.taskStatusLabel[status])
        }
      } else {
        return {
          icon: 'el-icon-success',
          type: 'success',
          message: this.$t(this.taskStatusLabel[status])
        }
      }
    },

    taskRoute(task) {
      return projectRoute({
        name: 'TaskHistoryView',
        query: { id: task.id }
      })
    },

    dateFormatter(value) {
      return projectTimezoneDate(value).format('DD MMM YYYY HH:mm:ss')
    },

    rowClickRoute(row) {
      if (this.$can('manageAutomation')) {
        return this.rowUpdateRoute(row)
      } else {
        return this.rowViewRoute(row)
      }
    },

    rowViewRoute(row) {
      return projectRoute({
        name: 'AutomationImportView',
        query: { id: row.id }
      })
    },

    rowUpdateRoute(row) {
      return projectRoute({
        name: 'AutomationImportUpdate',
        query: { id: row.id }
      })
    },

    rowRunHistoryRoute(row) {
      return projectRoute({
        name: 'AutomationImportRunHistorySyncs',
        query: { id: row.id }
      })
    },

    rowRunHistoryTasksRoute(row) {
      return projectRoute({
        name: 'AutomationImportRunHistoryTasks',
        query: { id: row.id }
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `import-automations/${row.id}`
      })
    },

    runAutomation(row) {
      this.restList.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `import-automations/${row.id}/run`
        })
      )
        .then(({ data }) => {
          this.$router.push(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data.id }
            })
          )
        })
        .catch(error => {
          this.restList.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    automationStatus(row) {
      if (row.active === true || row.active === 1) {
        return {
          icon: 'el-icon-turn-off',
          title: this.$t('Automations_Button_Deactivate')
        }
      } else {
        return {
          icon: 'el-icon-open',
          title: this.$t('Automations_Button_Activate')
        }
      }
    },

    resetDelta(row) {
      this.$confirm(
        this.$t('Automations_ResetDelta_Confirm'),
        this.$t('Automations_ResetDelta'),
        { type: 'warning' }
      )
        .then(() => {
          this.restList.loading = true
          RestApi(
            projectEndpoint({
              method: 'post',
              url: `import-automations/${row.id}/reset-delta`
            })
          )
            .then(() => {
              this.restList.loading = false
              this.$message({
                message: this.$t('Automations_ResetDelta_Success'),
                type: 'success'
              })
            })
            .catch(error => {
              this.restList.loading = false
              this.$message({
                message: error.message,
                type: 'error'
              })
            })
        })
        .catch(() => {})
    },

    toggleAutomationStatus(row) {
      this.restList.loading = true
      let currentStatus = row.active
      RestApi.patch(projectUrl(`import-automations/${row.id}`), {
        active: !row.active
      })
        .then(() => {
          this.restList.loading = false
          row.active = row.active === 1 ? 0 : 1
          this.$message({
            message:
              row.active === 0
                ? this.$t('RestList_DeactivateSuccessMessage')
                : this.$t('RestList_ActivateSuccessMessage'),
            type: 'success'
          })
        })
        .catch(error => {
          row.active = currentStatus
          this.restList.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  }
}
</script>

<style scoped>
.taskLink {
  text-decoration: none !important;
}
.taskLink .el-link {
  text-decoration: none !important;
  margin-right: 3px;
}
</style>



// WEBPACK FOOTER //
// src/modules/automations/components/AutomationImportsList.vue